import {useEffect, useMemo, useState} from "react";
import {useForm, Controller, useWatch} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {Col, Row} from "antd";
import {
	Button,
	Loading
} from "~/components";
import {
	InputField,
	GroupRadioField,
	TextAreaField,
	DateField,
	InputPriceField
} from "~/components/Forms";

function CustomerFormAddEdit({item, onAdd, onUpdate})
{
	const isEdit = Boolean(item?.id);

	const [typeValue, setTypeValue] = useState((isEdit) ? item.type : 'company');

	const typeOptions = [
		{value: 'company', label: 'Công Ty'},
		{value: 'personal', label: 'Cá Nhân'},
	];

	const initialValues = useMemo(
		() => ({
			type: item?.type ?? "company",
			code: item?.code ?? "",
			name: item?.name ?? "",
			nameEn: item?.nameEn ?? "",
			taxId: item?.taxId ?? "",
			email: item?.email ?? "",
			phone: item?.phone ?? "",
			address: item?.address ?? "",
			addressEn: item?.addressEn ?? "",
			note: item?.note ?? "",
			debtDuration: item?.debtDuration ?? 0,
			contractDate: item?.contractDate ? item?.contractDate*1000 : null,
			expireDate: item?.expireDate ? item?.expireDate*1000 : null,
		}),
		[item]
	);

	const validationSchema = Yup.object().shape({
		type: Yup.string().nullable().required('Bạn chưa chọn loại khách hàng'),
		code: Yup.string().nullable().required('Bạn điền mã khách hàng'),
		name: Yup.string()
			.nullable()
			.test('required-based-on-type', 'Tên không được bỏ trống', function (value) {
				const { type } = this.parent;
				if (!value && (type === 'company' || type === 'personal')) {
					return this.createError({
						message: type === 'company'
							? 'Tên công ty không được bỏ trống'
							: 'Tên khách hàng không được bỏ trống'
					});
				}
				return true;
			}),
		taxId: Yup.string().nullable()
			.test('required-taxid-based-on-type', 'Trường bắt buộc', function (value) {
				const { type } = this.parent;
				if (!value && (type === 'company' || type === 'personal')) {
					return this.createError({
						message: type === 'company'
							? 'Mã số thuế không được bỏ trống'
							: 'CCCD khách hàng không được bỏ trống'
					});
				}
				return true;
			}),
		address: Yup.string().required('Địa chỉ không được để trống'),
		debtDuration: Yup.number().required('Số ngày cho phép nợ không được để trống').min(0, 'Số ngày cho phép nợ phải lớn hơn hoặc bằng 0'),
	})

	const {control, handleSubmit, formState: {isSubmitting, errors}, reset, setValue} = useForm({
		defaultValues: initialValues,
		resolver: yupResolver(validationSchema)
	});

	//Thay đổi loại khách hàng
	const typeInput = useWatch({control, name: "type"});

	useEffect(() => {
		setTypeValue(typeInput);
	}, [typeInput]);

	useEffect(() =>
	{
		reset(initialValues);
	}, [item]);

	const renderCompanyInfo = (typeValue) => {
		const isCompany = typeValue === 'company';
		return (
			<>
				<p className="heading">Thông tin khách hàng</p>
				<Row gutter={10}>
					<Col span={12}>
						<Controller control={control} name="code" render={({ field }) => (
							<InputField
								label={"Mã khách hàng"}
								errors={errors}
								{...field}
							/>
						)} />
					</Col>
					<Col span={12}>
						<Controller control={control} name="taxId" render={({ field }) => (
							<InputField
								label={isCompany ? "Mã số thuế" : "CCCD khách hàng"}
								errors={errors}
								{...field}
							/>
						)} />
					</Col>
					<Col span={12}>
						<Controller control={control} name="name" render={({ field }) => (
							<InputField
								label={isCompany ? "Tên công ty" : "Tên khách hàng"}
								errors={errors}
								{...field}
							/>
						)} />
					</Col>
					<Col span={12}>
						<Controller control={control} name="nameEn" render={({ field }) => (
							<InputField
								label={isCompany ? "Tên quốc tế" : "Tên tiếng anh"}
								errors={errors}
								{...field}
							/>
						)} />
					</Col>
				</Row>
			</>
		);
	};

	const onSubmit = async (data) =>
	{
		const submitData = {
			...data,
			contractDate: data.contractDate ? Math.floor(data.contractDate / 1000) : null,
			expireDate: data.expireDate ? Math.floor(data.expireDate / 1000) : null,
		};

		if(isEdit)
		{
			await onUpdate(submitData, item);
		}
		else
		{
			await onAdd(submitData);
		}
	}

	return (
		<form className="form" onSubmit={handleSubmit(onSubmit)}>
			{isSubmitting && <Loading/>}
			<Controller control={control} name="type" render={({field}) => (
				<GroupRadioField options={typeOptions} errors={errors} {...field}/>
			)}/>
			{renderCompanyInfo(typeValue)}
			<p className="heading mt-2">Thông tin liên hệ</p>
			<Row gutter={10}>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="email" render={({field}) => (
						<InputField type="email" label="Email liên hệ" errors={errors} {...field}/>
					)}/>
				</Col>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="phone" render={({field}) => (
						<InputField type="tel" label="Số điện thoại" errors={errors} {...field}/>
					)}/>
				</Col>
			</Row>
			<Row gutter={10}>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="address" render={({field}) => (
						<InputField label="Địa chỉ" errors={errors} {...field}/>
					)}/>
				</Col>
				<Col className="gutter-row" span={12}>
					<Controller control={control} name="addressEn" render={({field}) => (
						<InputField label="Address" errors={errors} {...field}/>
					)}/>
				</Col>
			</Row>
			<p className="heading mt-2">Thông tin thêm</p>
			<Row gutter={10}>
				<Col className="gutter-row" span={8}>
					<Controller control={control} name="contractDate" render={({ field }) => (
						<DateField label="Ngày kí hợp đồng" errors={errors} {...field}/>
					)}/>
				</Col>
				<Col className="gutter-row" span={8}>
					<Controller control={control} name="expireDate" render={({ field }) => (
						<DateField label="Ngày hạn hợp đồng" errors={errors} {...field}/>
					)}/>
				</Col>
				<Col className="gutter-row" span={8}>
					<Controller control={control} name="debtDuration" render={({ field }) => (
						<InputPriceField label="Số ngày cho phép nợ" errors={errors} {...field}/>
					)}/>
				</Col>
				<Col className="gutter-row" span={24}>
					<Controller control={control} name="note" render={({field}) => (
						<TextAreaField label="Ghi chú" errors={errors} {...field}/>
					)}/>
				</Col>
			</Row>
			<div className="form-group d-flex justify-content-end">
				<Button primary type="submit">{isEdit ? 'Cập nhật' : 'Thêm khách hàng'}</Button>
			</div>
		</form>
	)
}

export default CustomerFormAddEdit;